import { useMemo, useRef, useState } from "react";
import { FileImage } from "@shared/ui/FileImage";
import { ApiError, api, type GroupDirect, type PublicUser, type Uuid } from "@shared/api";
import { fromPublic, Tile } from "@entities/user";
import { tint } from "@shared/lib/tint";
import { plural } from "@shared/lib/format";
import { Dialog, Field } from "@shared/ui/bits";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { IconCheck, IconClose, IconLogOut, IconMembers, IconOwner, IconPlus } from "@shared/ui/icons";
import { directName } from "../lib/title";

/** Сколько человек в группе вместе с владельцем — как в Discord. */
export const GROUP_MAX = 10;

/** Плашка группы: свой значок, а без него — значок людей на цвете, выведенном из номера переписки. */
export function GroupTile({ id, size = 30, iconId = null }: { id: Uuid; size?: number; iconId?: Uuid | null }) {
  return (
    <span className="tile">
      <span
        className="tile-box"
        style={{ width: size, height: size, borderRadius: "50%", background: tint(id), overflow: "hidden" }}
        aria-hidden
      >
        {iconId ? (
          <FileImage id={iconId} alt="" width={size} height={size} className="group-icon-image" />
        ) : (
          <IconMembers size={Math.round(size * 0.5)} />
        )}
      </span>
    </span>
  );
}

function reason(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.code === "not_friend") return "Позвать в группу можно только друзей.";
    if (error.code === "group_full") return `В группе не больше ${GROUP_MAX} человек.`;
    return error.message;
  }
  return fallback;
}

/**
 * Выбор друзей — для новой группы и для «Добавить в группу». Друзей, которые
 * уже в группе, здесь нет; выбрать больше, чем осталось мест, нельзя.
 */
export function PickFriends({
  title,
  action,
  friends,
  room,
  withName = false,
  onPick,
  onClose,
}: {
  title: string;
  action: string;
  friends: PublicUser[];
  /** Сколько ещё можно позвать. */
  room: number;
  /** Спросить и название — у новой группы. */
  withName?: boolean;
  onPick: (ids: Uuid[], name: string | null) => Promise<void>;
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState<Uuid[]>([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const needle = query.trim().toLowerCase();
  const shown = useMemo(
    () =>
      friends.filter(
        (friend) =>
          !needle ||
          (friend.display_name ?? "").toLowerCase().includes(needle) ||
          friend.handle.toLowerCase().includes(needle),
      ),
    [friends, needle],
  );

  function toggle(id: Uuid) {
    setChosen((previous) =>
      previous.includes(id) ? previous.filter((item) => item !== id) : previous.length < room ? [...previous, id] : previous,
    );
  }

  async function go() {
    setBusy(true);
    setProblem(null);
    try {
      await onPick(chosen, name.trim() || null);
      onClose();
    } catch (error) {
      setProblem(reason(error, "Не получилось"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      title={title}
      onClose={onClose}
      actions={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button type="button" disabled={busy || chosen.length === 0} onClick={() => void go()}>
            {action}
          </Button>
        </>
      }
    >
      <div className="group-pick">
        {withName && (
          <Field label="Название" hint="Можно не задавать — тогда группу назовут по участникам.">
            <Input value={name} maxLength={100} onChange={(event) => setName(event.target.value)} />
          </Field>
        )}
        <Input
          value={query}
          placeholder="Найти друга"
          aria-label="Найти друга"
          onChange={(event) => setQuery(event.target.value)}
        />
        <div className="small muted">
          {room > 0
            ? `Можно позвать ещё ${room - chosen.length} ${plural(room - chosen.length, "человека", "человек", "человек")}.`
            : "Мест в группе больше нет."}
        </div>
        <div className="group-pick-list" role="listbox" aria-multiselectable>
          {shown.length === 0 && <div className="small muted">Друзей, которых можно позвать, нет.</div>}
          {shown.map((friend) => {
            const on = chosen.includes(friend.id);
            return (
              <button
                key={friend.id}
                type="button"
                role="option"
                aria-selected={on}
                className={`group-pick-row ${on ? "on" : ""}`}
                disabled={!on && chosen.length >= room}
                onClick={() => toggle(friend.id)}
              >
                <Tile person={fromPublic(friend)} size={28} />
                <span className="grow ellipsis">{friend.display_name || friend.username}</span>
                <span className="tiny muted">{friend.handle}</span>
                <span className="group-pick-box">{on && <IconCheck size={13} />}</span>
              </button>
            );
          })}
        </div>
        {problem && <div className="small danger-text">{problem}</div>}
      </div>
    </Dialog>
  );
}

/** Название группы: пусто — называть по участникам. */
export function RenameGroup({ group, onClose }: { group: GroupDirect; onClose: () => void }) {
  const [name, setName] = useState(group.name ?? "");
  const [iconId, setIconId] = useState<Uuid | null>(group.icon_id ?? null);
  const picker = useRef<HTMLInputElement>(null);

  /** Значок ставится сразу, как в Discord: картинку выбрали — она уже у группы. */
  async function setIcon(file: File | null) {
    setBusy(true);
    setProblem(null);
    try {
      const uploaded = file ? await api.files.upload(file, "icon") : null;
      const updated = await api.directs.setGroupIcon(group.channel_id, uploaded?.id ?? null);
      setIconId(updated.channel_type === "group" ? (updated.icon_id ?? null) : null);
    } catch (error) {
      setProblem(reason(error, "Значок не поставился"));
    } finally {
      setBusy(false);
    }
  }
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function save() {
    setBusy(true);
    setProblem(null);
    try {
      await api.directs.renameGroup(group.channel_id, name.trim() || null);
      onClose();
    } catch (error) {
      setProblem(reason(error, "Не сохранилось"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      title="Название группы"
      onClose={onClose}
      actions={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button type="button" disabled={busy} onClick={() => void save()}>
            Сохранить
          </Button>
        </>
      }
    >
      <div className="line" style={{ gap: 12, alignItems: "center" }}>
        <GroupTile id={group.channel_id} size={48} iconId={iconId} />
        <Button type="button" variant="secondary" size="sm" disabled={busy} onClick={() => picker.current?.click()}>
          {iconId ? "Сменить значок" : "Поставить значок"}
        </Button>
        {iconId && (
          <Button type="button" variant="danger" size="sm" disabled={busy} onClick={() => void setIcon(null)}>
            Убрать
          </Button>
        )}
        <input
          ref={picker}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0] ?? null;
            event.target.value = "";
            if (file) void setIcon(file);
          }}
        />
      </div>
      <Field label="Название" hint="Пусто — группу назовут по участникам.">
        <Input
          value={name}
          maxLength={100}
          autoFocus
          placeholder={directName({ ...group, name: null })}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") void save();
          }}
        />
      </Field>
      {problem && <div className="small danger-text">{problem}</div>}
    </Dialog>
  );
}

/**
 * Правая колонка группы — участники, как в Discord: владелец с короной, у
 * владельца — «исключить» на строке, у всех — «Добавить друзей» и «Покинуть».
 */
export function GroupPanel({
  group,
  me,
  friends,
  onOpenProfile,
  onLeft,
}: {
  group: GroupDirect;
  me: PublicUser;
  friends: PublicUser[];
  onOpenProfile: (id: Uuid, anchor?: HTMLElement) => void;
  onLeft: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const everyone = [me, ...group.recipients];
  const owner = group.owner_id === me.id;
  const inside = new Set(everyone.map((person) => person.id));
  const room = GROUP_MAX - everyone.length;

  async function remove(id: Uuid) {
    setProblem(null);
    try {
      await api.directs.removeFromGroup(group.channel_id, id);
      if (id === me.id) onLeft();
    } catch (error) {
      setProblem(reason(error, "Не получилось"));
    }
  }

  return (
    <div className="group-panel">
      <div className="member-cap">
        УЧАСТНИКИ — {everyone.length}
      </div>
      {everyone.map((person) => (
        <div key={person.id} className="group-member">
          <button type="button" className="member group-member-main" onClick={(event) => onOpenProfile(person.id, event.currentTarget)}>
            <Tile person={fromPublic(person)} size={30} />
            <span className="grow ellipsis">{person.display_name || person.username}</span>
            {group.owner_id === person.id && (
              <span className="group-owner" title="Владелец группы">
                <IconOwner size={13} />
              </span>
            )}
          </button>
          {owner && person.id !== me.id && (
            <button
              type="button"
              className="icon-btn"
              aria-label={`Исключить ${person.display_name || person.username}`}
              title="Исключить из группы"
              onClick={() => void remove(person.id)}
            >
              <IconClose size={13} />
            </button>
          )}
        </div>
      ))}
      <div className="group-panel-actions">
        <Button type="button" variant="secondary" size="sm" disabled={room <= 0} onClick={() => setAdding(true)}>
          <IconPlus size={14} /> Добавить друзей
        </Button>
        <Button type="button" variant="danger" size="sm" onClick={() => void remove(me.id)}>
          <IconLogOut size={14} /> Покинуть группу
        </Button>
      </div>
      {problem && <div className="small danger-text">{problem}</div>}
      {adding && (
        <PickFriends
          title="Добавить в группу"
          action="Добавить"
          friends={friends.filter((friend) => !inside.has(friend.id))}
          room={room}
          onPick={async (ids) => {
            for (const id of ids) await api.directs.addToGroup(group.channel_id, id);
          }}
          onClose={() => setAdding(false)}
        />
      )}
    </div>
  );
}
